import { useState, useEffect, useMemo } from 'react'
import saddle_logo from '../../../assets/icons/saddleLogo.svg'
import pie_chart from '../../../assets/icons/pie-chart.svg'
import saddle from '../../../assets/icons/saddle.svg'
import horse from '../../../assets/icons/horse.svg'
import box from '../../../assets/images/box.png'
import {
  Link,
  NavLink,
  useLocation,
  useParams,
  useNavigate,
} from 'react-router-dom'
import logout from '../../../assets/icons/log-out.svg'
import { FaUser, FaUsers, FaFolderOpen, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa'
import { useUserContext } from '../../../context/UserContext'
import useMyFileGrants from '../../../hooks/useMyFileGrants'
import { useQuery } from '@apollo/client'
import { LIST_MANUFACTURERS_BASIC } from '../../../apollo/queries/listManufacturers'
import { formatString } from '../../../utils'

const toEncoded = (s = '') => encodeURIComponent(s?.toString().trim() || '')

const Sidebar = ({ collapsed = false, onToggle = () => { } }) => {
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { userData, onSignOutHandler, isManufacturerAdmin, isSaddlefitAdmin, isCactusAdmin, isTeamMember, isPrivateViewer } = useUserContext()
  const { hasGrants } = useMyFileGrants()


  const {
    data: manufacturersData,
    loading: manufacturersLoading,
    error: manufacturersError,
  } = useQuery(LIST_MANUFACTURERS_BASIC, {
    fetchPolicy: 'network-only',
    skip: userData?.brand !== 'saddlefitadmin',
  })

  const manufacturers = manufacturersData?.listManufacturers ?? []

  const pathName = `/saddles/manage/${encodeURIComponent(
    params?.encodedId || '',
  )}`
  const trackingOrderRoute = `/order/${params?.id ? encodeURIComponent(params.id) : ''
    }`
  const updateHorseRoute = `/horse-admin/horse/edit/${params?.horseId || ''}`
  const horseOwnershipRoute = `/horse-admin/horse/transferOwnership/${params?.horseId || ''
    }`

  // Build dynamic submenu (ONLY from API)
  const dynamicOrdersSubmenu = useMemo(
    () =>
      manufacturers.map(m => ({
        links: formatString(m?.brand) ?? 'Unknown',
        img: saddle,
        route: `/orders/${toEncoded(m?.brand)}`,
      })),
    [manufacturers],
  )

  const firstBrandRoute = dynamicOrdersSubmenu[0]?.route || ''
  const userBrandRoute = useMemo(() => {
    const brand = userData?.brand || ''
    return brand ? `/orders/${toEncoded(brand)}` : firstBrandRoute || '#'
  }, [userData?.brand, firstBrandRoute])

  // All routes that count as "inside Orders"
  const ordersRoutes = useMemo(
    () =>
      [
        trackingOrderRoute || '',
        ...dynamicOrdersSubmenu.map(d => d.route),
      ].filter(Boolean),
    [dynamicOrdersSubmenu],
  )

  const [isOrdersSubmenuOpen, setIsOrdersSubmenuOpen] = useState(
    ordersRoutes.includes(location.pathname),
  )

  useEffect(() => {
    if (ordersRoutes.includes(location.pathname)) {
      setIsOrdersSubmenuOpen(true)
    }
  }, [location.pathname, ordersRoutes])

  // Track selected Orders brand so it stays active on /order/:id
  const [selectedOrdersBrandRoute, setSelectedOrdersBrandRoute] = useState(
    () => localStorage.getItem('selectedOrdersBrandRoute') || '',
  )

  // When navigating within /orders, infer and remember the active brand
  useEffect(() => {
    if (location.pathname.startsWith('/orders/')) {
      const matching = dynamicOrdersSubmenu.find(sub =>
        location.pathname.startsWith(sub.route),
      )
      if (matching && matching.route !== selectedOrdersBrandRoute) {
        setSelectedOrdersBrandRoute(matching.route)
        try {
          localStorage.setItem('selectedOrdersBrandRoute', matching.route)
        } catch { }
      }
    }
  }, [location.pathname, dynamicOrdersSubmenu, selectedOrdersBrandRoute])

  const handleOrdersClick = e => {
    if (!firstBrandRoute) return
    e.preventDefault()
    setIsOrdersSubmenuOpen(true)
    if (location.pathname !== firstBrandRoute) {
      navigate(firstBrandRoute)
    }
  }

  // Additional routes used for active state highlighting
  const ordersAdditionalRoutes = useMemo(
    () => [...dynamicOrdersSubmenu.map(d => d.route)].filter(Boolean),
    [dynamicOrdersSubmenu],
  )

  const event_links = [
    ...(isManufacturerAdmin || isCactusAdmin || isTeamMember
      ? [
        { links: 'Dashboard', img: pie_chart, route: '/admin-dashboard' },
        { links: 'Saddles', img: saddle, route: '/saddle-admin' },
        { links: 'Banner', img: box, route: '/banner' },
        ...(!isTeamMember ? [{ links: 'Team Members', Icon: FaUsers, route: '/team-members' }] : []),
        ...(isCactusAdmin || (isTeamMember && userData?.brand === "cactus")
          ? [
            {
              links: 'Custom Saddle Orders',
              img: saddle,
              route: '/custom-saddle-orders',
            },
          ]
          : []),
        {
          links: 'Orders',
          img: box,
          route: userBrandRoute,
          directRoute: userBrandRoute,
          // No submenu for brand Admins
        },
        ...(hasGrants ? [{ links: 'Private Files', Icon: FaFolderOpen, route: '/file-browser' }] : []),
      ]
      : isPrivateViewer
        ? [
          { links: 'Dashboard', img: pie_chart, route: '/admin-dashboard' },
          { links: 'Private Files', Icon: FaFolderOpen, route: '/file-browser' },
        ]
      : isSaddlefitAdmin
        ? [
          { links: 'Dashboard', img: pie_chart, route: '/admin-dashboard' },
          { links: 'Event Creation', img: saddle, route: '/event-management' },
          { links: 'Bookings', img: saddle, route: '/booking-management' },
          {
            links: 'Manufacturers',
            Icon: FaUser,
            additionalRoutes: () => [
              '/manufacturers/create',
              params?.id ? `/manufacturers/${encodeURIComponent(params.id)}` : '',
            ].filter(Boolean),
            route: '/manufacturers',
          },
          {
            links: 'Saddle Admin',
            img: saddle,
            additionalRoutes: ['/saddles/manage', pathName],
            route: '/saddle-admin',
          },
          {
            links: 'Horses Admin',
            img: horse,
            route: '/horse-admin',
            additionalRoutes: [updateHorseRoute, horseOwnershipRoute],
          },
          {
            links: 'Custom Saddle Orders',
            img: saddle,
            route: '/custom-saddle-orders',
          },
          {
            links: 'Orders',
            img: box,
            route: firstBrandRoute || '#',
            additionalRoutes: ordersAdditionalRoutes,
            subMenu: dynamicOrdersSubmenu,
          },
          { links: 'Banner Section', img: box, route: '/banner-section' },
          { links: 'File Browser', Icon: FaFolderOpen, route: '/file-browser' },
          { links: 'Settings', img: box, route: '/settings' },
        ]
        : []),
  ]

  // Collapsed: slim icon-only rail with the hamburger at the top to expand again
  if (collapsed) {
    return (
      <div className="admin-dashboard-sidebar fixed top-0 left-0 hidden xl:flex flex-col w-[70px] h-screen">
        <div className="flex-none flex justify-center my-[40px]">
          <button
            type="button"
            title="Expand navigation"
            onClick={onToggle}
            className="bg-transparent border-none cursor-pointer text-[#808693] hover:text-white p-1"
          >
            <FaAngleDoubleRight className="w-5 h-5" />
          </button>
        </div>

        <div className="w-full flex-1 overflow-y-auto custom-scrollbar">
          {event_links.map((event, index) => {
            const additionalRoutes = Array.isArray(event.additionalRoutes)
              ? event.additionalRoutes
              : typeof event.additionalRoutes === 'function'
                ? event.additionalRoutes()
                : []

            const isOrdersLink = event.links === 'Orders'
            const isActive = isOrdersLink
              ? location.pathname.startsWith('/orders') ||
              location.pathname.startsWith('/order') ||
              additionalRoutes.includes(location.pathname)
              : location.pathname === event.route ||
              additionalRoutes.includes(location.pathname)

            return (
              <NavLink
                key={index}
                to={
                  isOrdersLink
                    ? (isManufacturerAdmin || isTeamMember || isCactusAdmin
                      ? (userBrandRoute || '#')
                      : (firstBrandRoute || '#'))
                    : event.route
                }
                title={event.links}
                className={() =>
                  `w-[44px] h-[40px] mx-auto my-1 rounded-[12px] cursor-pointer no-underline flex items-center justify-center ${isActive
                    ? 'bg-[#DEE6F0]'
                    : 'dashboard-links hover:bg-[#DEE6F0] hover:bg-opacity-10'
                  }`
                }
              >
                {event.Icon ? (
                  <event.Icon color="#808693" size={20} aria-label={event.links} />
                ) : (
                  <img
                    src={event.img || '/placeholder.svg'}
                    alt={event.links}
                    width={20}
                    height={20}
                  />
                )}
              </NavLink>
            )
          })}
        </div>

        <div className="pb-5 flex-none mt-4 flex justify-center">
          <Link
            to="#"
            onClick={onSignOutHandler}
            title="Log out"
            className="no-underline logout-btn flex items-center"
          >
            <img src={logout || '/placeholder.svg'} alt="logout" />
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-dashboard-sidebar fixed top-0 left-0 hidden xl:flex flex-col w-[290px] h-screen">
      <div className="flex-none flex items-center justify-between pr-5">
        <img
          src={saddle_logo || '/placeholder.svg'}
          alt="logo"
          className="ml-14 cursor-pointer my-[40px] w-[178.5px] h-[41.25px]"
        />
        <button
          type="button"
          title="Collapse navigation"
          onClick={onToggle}
          className="bg-transparent border-none cursor-pointer text-[#808693] hover:text-white p-1"
        >
          <FaAngleDoubleLeft className="w-5 h-5" />
        </button>
      </div>

      <div className="w-full flex-1 overflow-y-auto mx-auto custom-scrollbar">
        {event_links.map((event, index) => {
          const additionalRoutes = Array.isArray(event.additionalRoutes)
            ? event.additionalRoutes
            : typeof event.additionalRoutes === 'function'
              ? event.additionalRoutes()
              : []

          const isOrdersLink = event.links === 'Orders'
          const isActive = isOrdersLink
            ? location.pathname.startsWith('/orders') ||
            location.pathname.startsWith('/order') ||
            additionalRoutes.includes(location.pathname)
            : location.pathname === event.route ||
            additionalRoutes.includes(location.pathname)

          return (
            <div key={index}>
              <NavLink
                to={
                  event.links === 'Orders'
                    ? (isManufacturerAdmin || isTeamMember || isCactusAdmin ? (userBrandRoute || '#') : (firstBrandRoute || '#'))
                    : event.route
                }
                className={() =>
                  `rounded-tr-[20px] cursor-pointer rounded-br-[20px] w-[90%] h-[40px] no-underline flex items-center justify-center ${isActive
                    ? 'bg-[#DEE6F0]'
                    : 'dashboard-links my-1 hover:bg-[#DEE6F0] hover:bg-opacity-10'
                  }`
                }
                onClick={e => {
                  if (event.links === 'Orders') {
                    if (!(isManufacturerAdmin || isTeamMember || isCactusAdmin) && event.subMenu) {
                      handleOrdersClick(e)
                    } else if (isManufacturerAdmin || isTeamMember || isCactusAdmin) {
                      const target = userBrandRoute && userBrandRoute !== '#' ? userBrandRoute : firstBrandRoute
                      if (target) {
                        e.preventDefault()
                        navigate(target)
                      }
                    }
                  }
                }}
              >
                <span className="w-[210px] dashboard-links no-underline flex items-center">
                  <div className="w-[30px] flex items-center justify-center">
                    {event.Icon ? (
                      <event.Icon
                        color="#808693"
                        size={20}
                        aria-label={event.links}
                      />
                    ) : (
                      <img
                        src={event.img || '/placeholder.svg'}
                        alt={event.links}
                        width={20}
                        height={20}
                      />
                    )}
                  </div>
                  <span className={`ml-2 whitespace-nowrap ${isActive ? 'font-semibold' : ''}`}>
                    {event.links}
                  </span>
                </span>
              </NavLink>

              {/* Orders submenu (ONLY dynamic) */}
              {event.subMenu &&
                (isOrdersSubmenuOpen ||
                  additionalRoutes.includes(location.pathname)) && (
                  <div className="ml-16 mt-2 max-h-[200px] overflow-hidden">
                    <div className="h-full overflow-y-auto white-scrollbar">
                      {manufacturersLoading && !dynamicOrdersSubmenu.length && (
                        <div className="text-sm text-gray-500 py-2">
                          Loading manufacturers…
                        </div>
                      )}
                      {manufacturersError && (
                        <div className="text-sm text-red-500 py-2">
                          Failed to load manufacturers.
                        </div>
                      )}
                      {dynamicOrdersSubmenu.map((subItem, subIndex) => (
                        <NavLink
                          key={subIndex}
                          to={subItem.route}
                          className={({ isActive }) => {
                            const isDetailPage =
                              location.pathname.startsWith('/order')
                            const isBrandActive = isDetailPage
                              ? selectedOrdersBrandRoute === subItem.route
                              : isActive
                            return `rounded-full cursor-pointer w-[90%] h-[40px] no-underline flex items-center justify-center  ${isBrandActive
                              ? 'bg-[#DEE6F0]'
                              : 'dashboard-links my-1 hover:bg-[#DEE6F0]'
                              }`
                          }}
                          onClick={() => {
                            setSelectedOrdersBrandRoute(subItem.route)
                            try {
                              localStorage.setItem(
                                'selectedOrdersBrandRoute',
                                subItem.route,
                              )
                            } catch { }
                          }}
                        >
                          <span className="w-[210px] ml-6 dashboard-links no-underline flex items-center">
                            <img
                              src={subItem.img || '/placeholder.svg'}
                              alt={subItem.links}
                            />
                            <span className="ml-2 whitespace-nowrap hover:text-[#2B364B]">{subItem.links}</span>
                          </span>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )
        })}
      </div>

      <div className="pl-14 pb-5 flex-none mt-4">
        <Link
          to="#"
          onClick={onSignOutHandler}
          className="no-underline logout-btn text-[14px] space-x-1 flex items-center text-white capitalize"
        >
          <img src={logout || '/placeholder.svg'} alt="logout" />
          <span>Log out</span>
        </Link>
      </div>
    </div>
  )
}

export default Sidebar
